import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AlertFormInitializationFragment from '@app/site/secured/rateshopper/rates/alert/fragment/AlertFormInitializationFragment';
import AlertType from '@app/model/alert/AlertType';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import AlertInput from '@app/model/alert/input/AlertInput';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import { useTranslation } from '@app/component/translation/Translation';
import { useForm } from 'react-hook-form';
import { AlertQueryKeys } from '@app/model/alert/service/AlertQueryKeys';
import AlertService from '@app/model/alert/service/AlertService';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import _isEmpty from 'lodash/isEmpty';
import FormMultiSelect from '@app/component/form/input/select/FormMultiSelect';
import FormInputNumber from '@app/component/form/input/text/FormInputNumber';
import { CheckCircleIcon } from '@heroicons/react/20/solid';
import FormInputText from '@app/component/form/input/text/FormInputText';
import FormButton from '@app/component/form/button/FormButton';
import _get from 'lodash/get';
import Range from '@app/model/_common/Range';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import _set from 'lodash/set';
import AlertFilterLengthOfStayField from '@app/site/secured/rateshopper/rates/alert/fields/AlertFilterLengthOfStayField';
import AlertScheduleField from '@app/site/secured/rateshopper/rates/alert/fields/AlertScheduleField';
import AlertTimeFrameField from '@app/site/secured/rateshopper/rates/alert/fields/AlertTimeFrameField';
import FormSelect from '@app/component/form/input/select/FormSelect';
import { CompetitorsSoldOutAlertRuleInclusion } from '@app/model/alert/CompetitorsSoldOutAlertRuleInclusion';
import CompetitorsSoldOutAlertRuleInput from '@app/model/alert/input/CompetitorsSoldOutAlertRuleInput';
import AlertFilterRoomTypesField from '@app/site/secured/rateshopper/rates/alert/fields/AlertFilterRoomTypesField';
import { AlertRecipientsFragment } from '@app/site/secured/rateshopper/rates/alert/fragment/AlertRecipientsFragment';
import CompetitorType from '@app/model/company_hotel/CompetitorType';
import AlertAddExtraEmailsField from '@app/site/secured/rateshopper/rates/alert/fields/AlertAddExtraEmailsField';
import { AlertRecipientsType } from '@app/model/alert/AlertRecipientsType';
import AlertFilterMealsField from '@app/site/secured/rateshopper/rates/alert/fields/AlertFilterMealsField';


const CompetitorsSoldOutAlertPage: React.FunctionComponent<{
	alertId: number | undefined,
	isCopy?: boolean
}> = ({ alertId, isCopy = false }) => {

	const { companyHotel } = useSelectedCompanyHotel();

	if (!!companyHotel) {

		return <AlertFormInitializationFragment alertId={alertId}
																						alertType={AlertType.COMPETITORS_SOLD_OUT} companyHotel={companyHotel}
																						isCopy={isCopy} renderForm={(companyHotel, defaultValues) =>
			<CustomCompetitorsSoldOutAlert companyHotel={companyHotel} defaultValues={defaultValues} />} />;

	}

	return <></>;
};

const CustomCompetitorsSoldOutAlert: React.FunctionComponent<{
	companyHotel: CompanyHotel,
	defaultValues: AlertInput
}> = ({ companyHotel, defaultValues }) => {

	const toaster = useToaster();
	const { t } = useTranslation();
	const navigate = useNavigate();

	const form = useForm<AlertInput>({ defaultValues });

	const [thresholdRange, setThresholdRange] = useState<Range>({
		from: -300,
		to: 300,
	});


	const queryClient = useQueryClient();
	const saveAlertMutation = useMutation({
		mutationFn: AlertService.saveAlert,
		onSuccess: (data) => {
			toaster.success('Alert', 'Alert saved successfully!');
			queryClient.invalidateQueries({ queryKey: AlertQueryKeys.lists() });
			queryClient.setQueryData(AlertQueryKeys.detail(data.alert.id), data);
			navigate('/secured/rateshopper/rates/alerts');
		},
		onError: (error: string) => toaster.error('Alert', t(error)),
	});


	const onSubmit = (input: AlertInput) => {
		form.clearErrors();

		if (input.recipientsType === AlertRecipientsType.SELECTED && _isEmpty(input.recipientProfileIds)) {
			form.setError('recipientProfileIds', { message: 'Please select at least one recipient!' });
			return;
		}
		if (input.recipientsType === AlertRecipientsType.NONE && _isEmpty(input.extraEmails)) {
			form.setError('extraEmails', { message: 'Please add at least one extra recipient email!' });
			return;
		}
		if (!!input.schedule.weekdays)
			allToEmpty(input.schedule, 'weekdays');
		// @ts-ignore
		input.filter.lengthOfStay = parseInt(input.filter.lengthOfStay);
		if (input.competitorsSoldOutAlertRuleInput.competitorsInclusion === CompetitorsSoldOutAlertRuleInclusion.SOME) {
			// coerce inclusionPercentage from string -> number
			const pct = _get(input, 'competitorsSoldOutAlertRuleInput.inclusionPercentage');
			_set(input, 'competitorsSoldOutAlertRuleInput.inclusionPercentage', parseInt('' + pct, 10));
		} else
			_set(input, 'competitorsSoldOutAlertRuleInput.inclusionPercentage', 0.0);

		saveAlertMutation.mutate(input);

	};


	const allToEmpty = (obj: any, arrayProperty: string) => {
		if (_get(obj, arrayProperty).includes('ALL')) _set(obj, arrayProperty, undefined);
	};

	const isEdit = !!defaultValues.id;

	const inclusionWatch: CompetitorsSoldOutAlertRuleInclusion = form.watch('competitorsSoldOutAlertRuleInput.competitorsInclusion');

	const competitors = companyHotel.competitors
		.filter(competitor => competitor.type === CompetitorType.HOTEL)
		.map(competitor => competitor.competitors[0].hotel);

	return <div className="mx-auto max-w-7xl py-6 px-4 sm:px-6 lg:px-8">
		{/* Set Parity Alert */}
		<div className="mx-auto mt-8 lg:mx-0 lg:max-w-none bg-white p-4 sm:p-10 rounded-xl">
			<div className="mb-6">
				<h2 className="text-base font-semibold leading-7 text-gray-900">Add Sold Out Competitors Alert</h2>
				<p className="text-sm leading-6 text-gray-500">
					Configure and personalize your alert in the form provided below.
				</p>
			</div>
			<div className="pt-12 border-t border-gray-200">
				<div className="space-y-12">
					{/* Section - Rulesets */}
					<div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
						<div>
							<h2 className="text-base font-semibold leading-7 text-gray-900">Alert Setup</h2>
							<p className="mt-1 text-sm leading-6 text-gray-600">
								First, choose the providers you want to include in the alert. Then, set the percentage threshold
								that
								will
								trigger the alert.
							</p>
						</div>
						<div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
							{/* Providers */}
							<div className="col-span-full">
								{!_isEmpty(competitors) &&
									<FormMultiSelect name={`competitorsSoldOutAlertRuleInput.competitorIds`}
																	 form={form} placeholder="Select..."
																	 label="Select Competitors"
																	 isRequired
																	 disabled={isEdit}
																	 hideErrorMessage
																	 options={competitors!.map(competitor => {
																		 return {
																			 id: 'option_' + competitor.id,
																			 label: competitor.name,
																			 value: competitor.id,
																		 };
																	 })} />}
							</div>
							<div className="sm:col-span-3">
								<FormSelect name={`competitorsSoldOutAlertRuleInput.competitorsInclusion`} label="Include"
														isRequired form={form}
														disabled={isEdit}
														size="w-full"
														optionsSize="large"
														optionsTextSize="sm"
														placeholder="Select type" options={[
									{
										id: 'ALL',
										label: 'All',
										value: CompetitorsSoldOutAlertRuleInclusion.ALL,
									},
									{
										id: 'ANY',
										label: 'Any',
										value: CompetitorsSoldOutAlertRuleInclusion.ANY,
									},
									{
										id: 'SOME',
										label: 'Some',
										value: CompetitorsSoldOutAlertRuleInclusion.SOME,
									},
								]} />
							</div>
							<div className="sm:col-span-3">
								<FormInputNumber name={`competitorsSoldOutAlertRuleInput.inclusionPercentage`}
																 label={`Competitors Inclusion (%)`}
																 form={form}
																 min={thresholdRange.from}
																 max={thresholdRange.to}
																 isRequired={inclusionWatch === CompetitorsSoldOutAlertRuleInclusion.SOME}
																 disabled={isEdit || inclusionWatch !== CompetitorsSoldOutAlertRuleInclusion.SOME}
																 hideErrorMessage
																 placeholder="Insert value" />
							</div>
							<div className="col-span-full">
								<AlertRuleExplanation form={form} />
							</div>
						</div>
					</div>
					{/* Section - Details */}
					<div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
						<div>
							<h2 className="text-base font-semibold leading-7 text-gray-900">Alert Details</h2>
							<p className="mt-1 text-sm leading-6 text-gray-600">
								Please configure the different details for your alert.
							</p>
						</div>

						<div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
							{/* Room Types */}
							<div className="col-span-full">
								<AlertFilterRoomTypesField form={form} companyHotel={companyHotel} />
							</div>
							{/* Length of stay */}
							<div className="col-span-full">
								<AlertFilterLengthOfStayField form={form} />
							</div>
							<div className="col-span-full">
								<AlertFilterMealsField form={form} />
							</div>
						</div>
					</div>
					{/* Section - Timeframes */}
					<div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
						<div>
							<h2 className="text-base font-semibold leading-7 text-gray-900">Timeframe & Frequency</h2>
							<p className="mt-1 text-sm leading-6 text-gray-600">
								Set how often the alert runs and the time period it monitors.
							</p>
						</div>

						<div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
							{/* Frequency */}
							<div className="col-span-full">
								<AlertScheduleField form={form} defaultValues={defaultValues} />
							</div>
							<div className="col-span-full">
								<AlertTimeFrameField form={form} />
							</div>
						</div>
					</div>
					{!companyHotel.settings.alert.disableAlertProfileRecipientsSelection && <AlertRecipientsFragment form={form} />}
					<AlertAddExtraEmailsField form={form} />
					{/* Section - Push Notifications */}
					<div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
						<div>
							<h2 className="text-base font-semibold leading-7 text-gray-900">Finalize Alert</h2>
							<p className="mt-1 text-sm leading-6 text-gray-600">Assign a unique name to your alert. This
								will help you easily identify it
								later.</p>
						</div>
						<div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
							<div className="col-span-full">
								<FormInputText label="Alert Name" name="name" form={form} maxLength={100}
															 placeholder="Insert alert name" isRequired />
							</div>
						</div>
					</div>
				</div>
			</div>
			<div className="flex justify-end my-6 space-x-4">
				<FormButton state="text" to="/secured/rateshopper/rates/alerts"
										isLoading={saveAlertMutation.isPending}>Cancel</FormButton>
				<FormButton state="primary"
										onClick={form.handleSubmit(onSubmit)}
										isLoading={saveAlertMutation.isPending}>{isEdit ? 'Save' : 'Create'} Alert</FormButton>
			</div>
		</div>
	</div>;

};

const AlertRuleExplanation: React.FunctionComponent<{ form: any }> = ({ form }) => {

	const ruleInput: CompetitorsSoldOutAlertRuleInput = form.watch('competitorsSoldOutAlertRuleInput') ?? {};
	const competitorIds = ruleInput.competitorIds as any[];
	const competitorsInclusion = ruleInput.competitorsInclusion;
	const percentage = ruleInput.inclusionPercentage;

	if (!competitorIds?.length || !competitorsInclusion) {
		return null;
	}

	return <div className="w-full rounded-lg bg-white ring-1 ring-black ring-opacity-20">
		<div className="p-4">
			<div className="flex items-start">
				<div className="flex-shrink-0">
					<CheckCircleIcon className="h-6 w-6 text-green-400" />
				</div>
				<div className="ml-3 w-0 flex-1 pt-0.5">
					<p className="text-sm font-medium text-gray-900">Your alert translates
						to:</p>
					<p className="mt-1 text-sm text-gray-500">
						This alert will be triggered if there are
						{competitorsInclusion === CompetitorsSoldOutAlertRuleInclusion.ALL && ' all competitors sold out'}
						{competitorsInclusion === CompetitorsSoldOutAlertRuleInclusion.ANY && ' any competitor is sold out'}
						{competitorsInclusion === CompetitorsSoldOutAlertRuleInclusion.SOME && ` ${percentage}% competitors is sold out`}
					</p>
				</div>
			</div>
		</div>
	</div>;
};

export default CompetitorsSoldOutAlertPage;